import axios from 'axios';

let instance: AuthClient;

interface ImportProviderUsersOptions {
  users: Array<ImportedUser>;
  accountId: string;
  providerName: string;
}

export interface NewUser {
  AccountID: string;
  FirstName: string;
  LastName: string;
  CanSendMessageToAllPatients: boolean;
  Email: string;
  Telephone?: string;
  Address?: string;
}

export interface NewCsUser {
  FirstName: string;
  LastName: string;
  Email: string;
  Telephone?: string;
  Password?: string;
}

export interface ImportedUser {
  ID: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Telephone?: string;
}

interface AuthUser {
  ID: string;
  Email: string;
  Jwt: string;
}

interface AuthClientOptions {
  url: string;
}

export interface GetAccountUsersProviderOptions {
  accountId: string;
  providerName: string;
}

export interface AccountUserProvider {
  ID: string;
  FirstName: string;
  LastName: string;
  Email: string;
  Phone: string | null;
}

export class AuthClient {
  private url: string;
  private token: string | null;

  constructor(options: AuthClientOptions) {
    this.url = options.url;
    this.token = null;
  }

  getUrl(path: string) {
    console.log('getUrl', `${this.url}${path}`);
    return `${this.url}${path}`;
  }

  getToken(): string | null {
    return this.token;
  }

  setBaseUrl(url: string): AuthClient {
    this.url = url;
    return this;
  }

  setToken(token: string): AuthClient {
    this.token = token;
    return this;
  }

  async createUser(user: NewUser) {
    const url = this.getUrl('/user');
    const { data } = await axios({
      method: 'post',
      url,
      data: user,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }

  async importProviderUsers(opts: ImportProviderUsersOptions) {
    const url = this.getUrl('/user/import-from-provider');
    const { data } = await axios({
      method: 'post',
      url,
      data: {
        AccountID: opts.accountId,
        ProviderName: opts.providerName,
        Users: opts.users,
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }

  async getAccountUsersProvider(opts: GetAccountUsersProviderOptions) {
    const url = this.getUrl(`/account/${opts.accountId}/provider/${opts.providerName}/users`);
    const { data } = await axios({
      method: 'get',
      url,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data.data as Array<AccountUserProvider>;
  }

  async verifyToken(token: string): Promise<AuthUser> {
    const url = this.getUrl('/verify_token');
    const { data } = await axios.post(url, {
      token,
    });
    return data as AuthUser;
  }

  async verifyCode(code: string): Promise<AuthUser> {
    const url = this.getUrl('/verify_code');
    console.log('Auth token:', this.token);
    const { data } = await axios({
      method: 'post',
      url,
      data: {
        code,
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    this.token = data.Jwt;
    return data as AuthUser;
  }

  async loginWithCredentialsCs(email: string, password: string) {
    const url = this.getUrl('/login_cs');
    const { data } = await axios.post(url, {
      email,
      password,
    });
    this.token = data.Jwt;
    return data;
  }

  async loginWithCredentials(options: {
    email: string;
    password: string;
    platformInformation: string;
  }) {
    //console.log("loginWithCredentials", options)
    const url = this.getUrl('/login');
    const { data } = await axios.post(url, options);
    this.token = data.Jwt;
    return data;
  }

  async loginWithPhone(phone: string) {
    const url = this.getUrl('/login_phone');
    const { data } = await axios.post(url, {
      phone,
    });
    this.token = data.Jwt;
    return data;
  }

  async requestResetPassword(email: string) {
    const url = this.getUrl('/request_reset_password');
    const { data } = await axios.post(url, {
      email,
    });
    return data;
  }

  async resetPassword(options: { password: string; tempToken: string }) {
    const { password, tempToken } = options;
    const url = this.getUrl('/reset_password');
    const { data } = await axios({
      method: 'put',
      url,
      data: {
        password,
      },
      headers: {
        Authorization: `Bearer ${tempToken}`,
      },
    });
    return data;
  }

  async resetTempPassword(options: { userId: string }) {
    const { userId } = options;
    const url = this.getUrl('/reset_temp_password');
    const { data } = await axios({
      method: 'put',
      url,
      data: {
        userId,
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }


  async createCsUser(userData: NewCsUser) {
    const url = this.getUrl('/cs_user');
    const { data } = await axios({
      method: 'post',
      url,
      data: userData,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }

  async requestResetCsUserPassword(email: string) {
    const url = this.getUrl('/request_cs_reset_password');
    const { data } = await axios.post(url, {
      email,
    });
    return data;
  }

  async resetCsUserPassword(options: {email:string; password: string; tempToken: string }) {
    const { email, password, tempToken } = options;
    const url = this.getUrl('/reset_cs_password');
    const { data } = await axios({
      method: 'put',
      url,
      data: {
        email,
        password
      },
      headers: {
        Authorization: `Bearer ${tempToken}`,
      },
    });
    return data;
  }

  async updatePassword(options: { oldPassword: string; newPassword: string; isOnDuty :boolean }) {
    const { oldPassword, newPassword, isOnDuty } = options;
    const url = this.getUrl('/update_password');
    const { data } = await axios({
      method: 'put',
      url,
      data: {
        oldPassword,
        newPassword,
        isOnDuty
      },
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }

  async canUserLoginWithProvider(email: string) {
    const url = this.getUrl(`/user/can-login-with-provider?email=${email}`);
    const { data } = await axios({
      method: 'get',
      url,
    });
    return data.data;
  }

  async userAccountRequiresHexnode(email: string) {
    const url = this.getUrl(`/user/account-hexnode-enabled?email=${email}`);
    const { data } = await axios({
      method: 'get',
      url,
    });
    return data.data;
  }

  async resendCode() {
    const url = this.getUrl('/resend_code');
    const { data } = await axios({
      method: 'post',
      url,
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
    });
    return data;
  }

  async logout(options: {
    userId:string;
    platformInformation: string;
  }) {
    const url = this.getUrl('/logout');
    await axios.post(url, {
      token: this.token,
      userId: options.userId,
      platformInformation: options.platformInformation
    });
    this.token = null;
  }

  async logoutCs() {
    const url = this.getUrl('/logout_cs');
    await axios.post(url, {
      token: this.token,
    });
    this.token = null;
  }

}

export function getInstanceOnly(): AuthClient {
  return instance;
}

export function getInstance(options: AuthClientOptions): AuthClient {
  if (instance) {
    return instance;
  }
  instance = new AuthClient(options);
  return instance;
}

export function setBaseUrl(url: string) {
  if (!instance) {
    instance = new AuthClient({
      url
    });
  } else {
    instance.setBaseUrl(url);
  }
}

export function setToken(token: string) {
  if (!instance) {
    throw new Error('Client needs to be initialized first');
  }
  instance.setToken(token);
}
